/**
 * ContentPhotoStrip — renders approved user photos for a given
 * target (article, travel_operator, business…) at a specific slot
 * (top / middle / bottom / sidebar / header / inline).
 *
 * Falls back to null if no photos exist so it can be safely embedded
 * anywhere without producing visual noise.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Camera } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContentPhotoStrip({
  targetType,
  targetSlug,
  targetId,
  slot = "middle",
  max = 4,
  className = "",
}) {
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    const params = { target_type: targetType, slot, limit: max };
    if (targetSlug) params.target_slug = targetSlug;
    if (targetId)   params.target_id   = targetId;
    axios.get(`${API}/api/photos`, { params })
      .then(r => setPhotos(r.data.items || []))
      .catch(() => {});
  }, [targetType, targetSlug, targetId, slot, max]);

  if (!photos.length) return null;

  const count = photos.length;
  const gridCols =
    count === 1 ? "grid-cols-1" :
    count === 2 ? "grid-cols-2" :
    count === 3 ? "grid-cols-3" : "grid-cols-2 md:grid-cols-4";

  return (
    <section className={`my-6 ${className}`} data-testid={`content-photos-${slot}`}>
      <div className="flex items-center gap-2 mb-2">
        <Camera className="w-3.5 h-3.5 opacity-60" />
        <div className="overline text-xs opacity-70">CONTRIBUTED PHOTOS</div>
      </div>
      <div className={`grid gap-2 ${gridCols}`}>
        {photos.map(p => (
          <figure key={p.id} className="relative rounded-lg overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }} data-testid={`photo-${p.id}`}>
            {p.ad_code ? (
              <div dangerouslySetInnerHTML={{ __html: p.ad_code }} />
            ) : (p.link_url && !p.data_uri) ? (
              <a href={p.link_url} target="_blank" rel="noreferrer" className="block p-4 text-sm text-center hover:bg-white/40">{p.title}</a>
            ) : (
              <img src={p.data_uri} alt={p.title} className="w-full h-40 object-cover" loading="lazy" />
            )}
            {p.data_uri && (
              <figcaption className="px-2 py-1 text-[10px] opacity-70 flex items-center justify-between gap-1">
                <span className="truncate">{p.title}</span>
                <span className="whitespace-nowrap opacity-70">© {p.credit_name}</span>
              </figcaption>
            )}
          </figure>
        ))}
      </div>
    </section>
  );
}
